import { faLocationArrow, faStarship, faStore } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { isInOrbit, format, tradeGoodInContract, formatNumber } from "../../utils/helpers";
import { styled } from "styled-components";
import { Tabs, TabItem, TabContent } from "../common/Tabs";
import Button, { ButtonGroup } from "../common/Button";
import Flow from "../common/Flow";
import React, { useState } from "react";
import Table from "../common/Table";
import Typography from "../common/Typography";
import WaypointType from "./WaypointType";
import { transition } from "../../styles/mixins";

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  justify-content: space-between;
  overflow: scroll;
`;

const WaypointImage = styled.div`
  align-items: center;
  aspect-ratio: 2.75 / 1;
  background-image: url('../images/background.png');
  background-size: cover;
  border-bottom: var(--border-width) var(--border-style) var(--border-color);
  border-top: var(--border-width) var(--border-style) var(--border-color);
  display: flex;
  justify-content: center;
  overflow: hidden;

  > div {
    ${transition('width')}
    transition-delay: ${props => props.$resize ? '250ms' : '0ms'};
    width: ${props => props.$resize ? '168px' : '48px'};
  }
`;

const Traits = styled.div`
  padding-block: var(--space-3);
  border-block: var(--border);
`;

function WaypointDetail({ store, resize }) {
  const [tab, setTab] = useState('traits');
  const waypoint = store.state.waypoint;
  const selectedShip = store.getSelectedShip();
  const gateInfo = waypoint && waypoint !== null ? store.getConnectedSystems(waypoint.symbol) : null;
  const survey = waypoint ? store.getSurvey(waypoint.symbol) : null;
  const importantTraits = waypoint && waypoint.traits.filter(e => e.symbol == 'MARKETPLACE' || e.symbol == 'SHIPYARD');
  const contracts = store.state.contracts;

  const jump = (systemSymbol) => {
    store.jump(systemSymbol);
  };

  return (
    <Root>
      <div>
        <Flow $space={3}>
          <Typography as="h2" fontSize="xl">{waypoint && waypoint.symbol}</Typography>
          {waypoint && (
            <WaypointImage $resize={resize}>
              <WaypointType symbol={waypoint && waypoint.symbol} type={waypoint && waypoint.type} />
            </WaypointImage>
          )}
          <Table>
            <tbody>
              <tr>
                <th>Type</th>
                <td>{waypoint && waypoint.type}</td>
              </tr>
              <tr>
                <th>System</th>
                <td>{waypoint && waypoint.systemSymbol}</td>
              </tr>
              <tr>
                <th>Faction</th>
                <td>{waypoint && waypoint.faction && waypoint.faction.symbol}</td>
              </tr>
            </tbody>
          </Table>
          {importantTraits && importantTraits.length > 0 && (
            <>
              <Typography as="h2">Important traits</Typography>
              <Traits>
                <ButtonGroup>
                  {importantTraits && importantTraits.map((trait, idx) => (
                    <React.Fragment key={idx}>
                      {trait.symbol == 'MARKETPLACE' && <Button $icon onClick={store.fetchMarket}><FontAwesomeIcon size="lg" icon={faStore} /></Button>}
                      {trait.symbol == 'SHIPYARD' && <Button $icon onClick={store.fetchShipyard}><FontAwesomeIcon size="lg" icon={faStarship} /></Button>}
                    </React.Fragment>
                  ))}
                </ButtonGroup>
              </Traits>
            </>
          )}
        </Flow>
        {resize && (
          <Flow $space={6}>
            <Tabs>
              {waypoint && waypoint.traits.length > 0 && 
                <TabItem $active={tab === 'traits'} onClick={() => setTab('traits')} className="active">Traits</TabItem>
              }
              <TabItem $active={tab === 'orbitals'} onClick={() => setTab('orbitals')}>Orbitals</TabItem>
              <TabItem $active={tab === 'charted'} onClick={() => setTab('charted')}>Charted</TabItem>
              {waypoint && (waypoint.type === 'JUMP_GATE') && 
                <TabItem $active={tab === 'systems'} onClick={() => setTab('systems')}>Connected Systems</TabItem>
              }
              {waypoint && survey && 
                <TabItem $active={tab === 'survey'} onClick={() => setTab('survey')}>Survey</TabItem>
                }
            </Tabs>
            {tab === 'traits' && waypoint && waypoint.traits.length > 0 && (
              <TabContent>
                <Table $style="striped">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Descripion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waypoint && waypoint.traits.map((trait, idx) => (
                      <tr key={idx}>
                        <td><strong>{trait.name}</strong></td>
                        <td>{trait.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TabContent>
            )}
            {tab === 'orbitals' && (
              <TabContent>
                <Table $style="striped">
                  <thead>
                    <tr>
                      <th>Symbol</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waypoint && waypoint.orbitals.length != 0 ? waypoint.orbitals.map((orbit, idx) => (
                      <tr key={idx}>
                        <td>{orbit.symbol}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={2}>No waypoints in orbit of {waypoint.symbol}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </TabContent>
            )}
            {tab === 'charted' && (
              <TabContent>
                <Table>
                  <tbody>
                    <tr>
                      <th>Charted by</th>
                      <td>{waypoint && waypoint.chart.submittedBy}</td>
                    </tr>
                    <tr>
                      <th>Charted on</th>
                      <td>{waypoint && format(waypoint.chart.submittedOn)}</td>
                    </tr>
                  </tbody>
                </Table>
              </TabContent>
            )}

            {tab === 'systems' && waypoint && (waypoint.type === 'JUMP_GATE') && (
              <TabContent>
                <Table>
                  <thead>
                    <tr>
                      <th>Symbol</th>
                      <th>Type</th>
                      <th>Distance</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gateInfo && gateInfo.connectedSystems.map((system, idx) => (
                      <tr key={idx}>
                        <td>{system.symbol}</td>
                        <td>{system.type}</td>
                        <td>{formatNumber(system.distance)}</td>
                        <td>
                          <Button $action="icon" $disabled={!isInOrbit(selectedShip && selectedShip)} onClick={() => jump(system.symbol)}>
                            <FontAwesomeIcon icon={faLocationArrow} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TabContent>
            )}
            {tab === 'survey' && waypoint && survey && (
              <TabContent>
                <Flow $space={3}>
                  <Table>
                    <tbody>
                      <tr>
                        <th>Size</th>
                        <td>{survey.size}</td>
                      </tr>
                      <tr>
                        <th>Expiration</th>
                        <td>{format(survey.expiration)}</td>
                      </tr>
                      <tr>
                        <th>Size</th>
                        <td>{survey.size}</td>
                      </tr>
                    </tbody>
                  </Table>
                  <Table $style="striped">
                    <thead>
                      <tr>
                        <th>Deposits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {survey.deposits.map((deposit, idx) => (
                        <tr key={idx} className={tradeGoodInContract(deposit.symbol, contracts) ? 'highlight' : undefined}>
                          <td>{deposit.symbol}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Flow>
              </TabContent>
            )}
          </Flow>
        )}
      </div>
    </Root>
  );
}

export default WaypointDetail;
